import React, { useEffect, useRef, useState } from "react";
import { KeyboardArrowRight } from "@mui/icons-material";
import { Alert, Box, Button } from "@mui/material";
import WorkflowPifAdditionalSection from "./WorkflowPifAdditionalSection";
import WorkflowPifContactSection from "./WorkflowPifContactSection";
import WorkflowPifDrivingSection from "./WorkflowPifDrivingSection";
import WorkflowPifIdentitySection from "./WorkflowPifIdentitySection";
import WorkflowPifPaySection from "./WorkflowPifPaySection";
import WorkflowPifPositionSection from "./WorkflowPifPositionSection";
import {
  missingPifDefaults,
  PIF_BOOLEAN,
  PifDetailsReference,
  PifDetailsValues,
  pifDetailsMode,
  validatePifDetails,
} from "./pifDetailsModel";
import { PifIdentityMatch } from "./workflowRunData";
import { PIF_DETAILS_FIELDS } from "./pifWorkflowKeys";

const F = PIF_DETAILS_FIELDS;

/** The driving answers only apply to the position they were given for. */
const DRIVING_KEYS = [F.hasLicense, F.willOperateVehicle, F.licenseNumber];

/** Changing one of these empties the answers that were picked from its list, or that it decided. */
const DEPENDENT_KEYS: Record<string, string[]> = {
  [F.department]: [F.position, F.reportsTo, F.service, F.client, ...DRIVING_KEYS],
  [F.position]: [F.service, F.client, ...DRIVING_KEYS],
  [F.service]: [F.client],
};

interface WorkflowPifDetailsStageProps {
  identity: PifIdentityMatch;
  /** pif-3's answers by field key. Owned by the caller, so a real case can save them as typed. */
  values: PifDetailsValues;
  onChange: (patch: PifDetailsValues) => void;
  reference: PifDetailsReference;
  /** Passes the screen's own rules; the caller submits. */
  onContinue: () => void;
  /** Shown only when given (a real case). */
  onSaveDraft?: () => void;
  busy?: boolean;
  /** Send for approval is off while something the case needs is missing — e.g. a new hire's SSN on
   *  the applicant record (Pooja, 2026-10-08). Save draft stays available. */
  continueBlocked?: boolean;
  /** The server's field errors from the last submit, by field key. */
  serverErrors?: Record<string, string>;
  /** Save / submit feedback from the caller, above the actions. */
  banner?: React.ReactNode;
}

/**
 * pif-3 PIF Details on the confirmed field list (decisions log section 2): identity and contact by
 * flow, position, pay, driving and additional. Built on the workflow side, so the live PostIF form
 * is untouched; only PostIF's pure rule helpers are shared.
 */
const WorkflowPifDetailsStage: React.FC<WorkflowPifDetailsStageProps> = ({
  identity,
  values,
  onChange,
  reference,
  onContinue,
  onSaveDraft,
  busy = false,
  continueBlocked = false,
  serverErrors = {},
  banner,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const filledForRef = useRef<string | null>(null);

  // The identity the case was routed on fills the keys it hasn't saved yet — once per identity, so
  // a field the user then clears stays cleared.
  useEffect(() => {
    const identityKey = `${identity.flow}:${identity.employeeId}`;
    if (filledForRef.current === identityKey) return;
    filledForRef.current = identityKey;
    const fill = missingPifDefaults(values, identity);
    if (fill) onChange(fill);
  }, [identity, values, onChange]);

  const handleChange = (fieldKey: string, value: string) => {
    const cleared = (DEPENDENT_KEYS[fieldKey] ?? []).filter((key) => values[key]);
    // The licence number is asked only while either driving answer is yes; once neither is, a
    // hidden number must not stay on the case.
    const next = { ...values, [fieldKey]: value };
    if (
      (fieldKey === F.hasLicense || fieldKey === F.willOperateVehicle) &&
      next[F.hasLicense] !== PIF_BOOLEAN.yes &&
      next[F.willOperateVehicle] !== PIF_BOOLEAN.yes &&
      values[F.licenseNumber]
    ) {
      cleared.push(F.licenseNumber);
    }
    onChange({ [fieldKey]: value, ...Object.fromEntries(cleared.map((key) => [key, ""])) });
    if (errors[fieldKey]) {
      setErrors((current) => {
        const remaining = { ...current };
        delete remaining[fieldKey];
        return remaining;
      });
    }
  };

  const handleContinue = () => {
    const found = validatePifDetails(values, identity, reference);
    setErrors(found);
    if (Object.keys(found).length === 0) onContinue();
  };

  const shownErrors = { ...serverErrors, ...errors };
  const sectionProps = { identity, values, errors: shownErrors, reference, onChange: handleChange };
  const mode = pifDetailsMode(identity.flow);

  return (
    <>
      <WorkflowPifIdentitySection {...sectionProps} />
      {mode.collectsContact && <WorkflowPifContactSection {...sectionProps} />}
      <WorkflowPifPositionSection {...sectionProps} />
      <WorkflowPifPaySection {...sectionProps} />
      {reference.driving && <WorkflowPifDrivingSection {...sectionProps} />}
      <WorkflowPifAdditionalSection {...sectionProps} />

      {Object.keys(errors).length > 0 && (
        <Alert severity="warning" className="workflow-run__alert">
          Complete the highlighted fields before sending for approval.
        </Alert>
      )}
      {banner}
      <Box className="workflow-run__actions workflow-run__actions--end">
        {onSaveDraft && (
          <Button variant="outlined" color="secondary" onClick={onSaveDraft} disabled={busy}>
            Save draft
          </Button>
        )}
        <Button
          variant="contained"
          endIcon={<KeyboardArrowRight />}
          onClick={handleContinue}
          disabled={busy || continueBlocked}
        >
          Send for approval
        </Button>
      </Box>
    </>
  );
};

export default WorkflowPifDetailsStage;
